import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Avatar,
  Divider
} from '@mui/material';
import {
  Upload as UploadIcon,
  Save as SaveIcon,
  PhotoCamera as PhotoCameraIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const CompanySettingsPage = () => {
  const { refreshSettings } = useSettings();
  
  const [settings, setSettings] = useState({
    companyName: 'Paanap',
    tagline: '',
    logoUrl: '',
    primaryColor: '#64A8ED',
    secondaryColor: '#C51668',
    accentColor: '#FB9637',
    backgroundColor: '#F5F5F5',
    primaryFont: 'Inter',
    headingFont: 'Inter',
    showLogoInHeader: true,
    showCompanyName: true,
    headerBackgroundColor: '#64A8ED'
  });

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoBase64, setLogoBase64] = useState(null); // ✅ NEW: Store base64
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const settingsData = response.data.data?.settings || response.data.data;
      
      if (settingsData) {
        setSettings(settingsData);
        setLogoPreview(settingsData.logoUrl);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    }
  };

  // ✅ UPDATED: Convert file to base64
  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo too large. Maximum size is 2MB.');
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPG, PNG, GIF)');
        return;
      }

      setLogoFile(file);
      
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setLogoPreview(base64String);
        setLogoBase64(base64String);
      };
      reader.readAsDataURL(file);
    }
  };

  // ✅ UPDATED: Send base64 to backend
  const handleUploadLogo = async () => {
    if (!logoBase64) {
      setError('Please select a logo file first');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');

      // ✅ Send base64 data instead of FormData
      const response = await axios.post(`${API_BASE_URL}/settings/logo`, 
        { logoBase64 }, // Send as JSON
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json' // JSON instead of multipart
          }
        }
      );

      setSettings(prev => ({ ...prev, logoUrl: logoBase64 }));
      setSuccess('Logo uploaded successfully!');
      setLogoFile(null);
      
      // Refresh global settings
      await refreshSettings();
      
    } catch (err) {
      console.error('Error uploading logo:', err);
      setError(err.response?.data?.message || 'Failed to upload logo');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess('Settings saved successfully! Applying changes...');
      
      await refreshSettings();
      
      setTimeout(() => {
        setSuccess('Settings applied successfully!');
      }, 500);
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Company Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize your application's branding and appearance
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Logo Upload Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Company Logo
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={logoPreview}
                alt="Company Logo"
                sx={{ width: 150, height: 150, border: '2px solid #ddd' }}
                variant="rounded"
              >
                {!logoPreview && <PhotoCameraIcon sx={{ fontSize: 60 }} />}
              </Avatar>
              
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="logo-upload"
                type="file"
                onChange={handleLogoChange}
              />
              <label htmlFor="logo-upload">
                <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                  Choose Logo
                </Button>
              </label>

              {logoFile && (
                <Button
                  variant="contained"
                  onClick={handleUploadLogo}
                  disabled={loading}
                  startIcon={<SaveIcon />}
                >
                  {loading ? 'Uploading...' : 'Upload Logo'}
                </Button>
              )}
              
              <Typography variant="caption" color="text.secondary">
                Max size: 2MB. Formats: JPG, PNG, GIF
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Company Info */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Company Information
            </Typography>
            <TextField
              fullWidth
              label="Company Name"
              value={settings.companyName}
              onChange={(e) => handleChange('companyName', e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Tagline (Optional)"
              value={settings.tagline || ''}
              onChange={(e) => handleChange('tagline', e.target.value)}
              multiline
              rows={2}
            />
          </Paper>
        </Grid>

        {/* Brand Colors */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Brand Colors
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Primary Color"
                  type="color"
                  value={settings.primaryColor}
                  onChange={(e) => handleChange('primaryColor', e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  Main brand color (Blue)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Secondary Color"
                  type="color"
                  value={settings.secondaryColor}
                  onChange={(e) => handleChange('secondaryColor', e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  Secondary brand color (Pink)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Accent Color"
                  type="color"
                  value={settings.accentColor}
                  onChange={(e) => handleChange('accentColor', e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  Accent color (Orange)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Header Background"
                  type="color"
                  value={settings.headerBackgroundColor}
                  onChange={(e) => handleChange('headerBackgroundColor', e.target.value)}
                />
                <Typography variant="caption" color="text.secondary">
                  Header bar color
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Typography */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Typography
            </Typography>
            <TextField
              fullWidth
              label="Primary Font"
              value={settings.primaryFont}
              onChange={(e) => handleChange('primaryFont', e.target.value)}
              sx={{ mb: 2 }}
              helperText="e.g., Inter, Roboto, Poppins"
            />
            <TextField
              fullWidth
              label="Heading Font"
              value={settings.headingFont}
              onChange={(e) => handleChange('headingFont', e.target.value)}
              helperText="Font for headings and titles"
            />
          </Paper>
        </Grid>

        {/* Display Options */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Display Options
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showLogoInHeader}
                  onChange={(e) => handleChange('showLogoInHeader', e.target.checked)}
                />
              }
              label="Show logo in header"
            />
            <br />
            <FormControlLabel
              control={
                <Switch
                  checked={settings.showCompanyName}
                  onChange={(e) => handleChange('showCompanyName', e.target.checked)}
                />
              }
              label="Show company name in header"
            />
          </Paper>
        </Grid>

        {/* Save Button */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={fetchSettings}
              disabled={loading}
            >
              Reset
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveSettings}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            >
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CompanySettingsPage;